import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import classNames from "classnames";

import { actions as userActions } from "store/users";

import CloseIcon from "assets/images/icons/JSX/Close";
import ArrowDown from "assets/images/icons/JSX/arrow-down";

import classes from "./styles.module.scss";

const { saveUserMeta } = userActions;

const AUTO_ADVANCE_MS = 7000;

/**
 * Slim, dismissible feature carousel for announcing release highlights.
 *
 * Self-contained: reads/writes dismiss state on userMeta.whatsNewDismissed[versionId]
 * unless `dismissed` / `onDismiss` are provided for controlled use.
 *
 * @param {string} versionId - Unique release id used for dismiss persistence (e.g. "1.0.0")
 * @param {Array} slides - [{ id, title, description, cta?: { label, to }, Icon? }]
 * @param {string} [eyebrow] - Small label shown above the title (e.g. "New in 1.0")
 * @param {number} [autoAdvanceMs] - Auto-advance interval; set 0 to disable
 * @param {boolean} [dismissed] - Controlled dismiss override
 * @param {Function} [onDismiss] - Controlled dismiss handler
 * @param {string} [className]
 */
const WhatsNewCarousel = ({
    versionId,
    slides = [],
    eyebrow = "What's new",
    autoAdvanceMs = AUTO_ADVANCE_MS,
    dismissed: dismissedProp,
    onDismiss: onDismissProp,
    className,
}) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const uid = useSelector((state) => state.user?.uid);
    const whatsNewDismissed = useSelector((state) => state.user?.userMeta?.whatsNewDismissed || {});

    const isControlled = typeof dismissedProp === "boolean";
    const isDismissed = isControlled ? dismissedProp : Boolean(whatsNewDismissed[versionId]);

    const [activeIndex, setActiveIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [isFading, setIsFading] = useState(false);
    const fadeTimeoutRef = useRef(null);

    const slideCount = slides.length;
    const activeSlide = slides[activeIndex] || null;

    const goTo = useCallback(
        (nextIndex) => {
            if (!slideCount) return;
            const normalized = ((nextIndex % slideCount) + slideCount) % slideCount;
            if (normalized === activeIndex) return;

            clearTimeout(fadeTimeoutRef.current);
            setIsFading(true);
            fadeTimeoutRef.current = setTimeout(() => {
                setActiveIndex(normalized);
                setIsFading(false);
            }, 150);
        },
        [activeIndex, slideCount]
    );

    const goNext = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);
    const goPrev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);

    useEffect(() => {
        if (!autoAdvanceMs || isPaused || isDismissed || slideCount <= 1) return undefined;

        const timer = setInterval(goNext, autoAdvanceMs);
        return () => clearInterval(timer);
    }, [autoAdvanceMs, goNext, isDismissed, isPaused, slideCount]);

    useEffect(() => {
        return () => clearTimeout(fadeTimeoutRef.current);
    }, []);

    const handleDismiss = () => {
        if (onDismissProp) {
            onDismissProp(versionId);
            return;
        }

        if (!uid || !versionId) return;

        dispatch(
            saveUserMeta({
                uid,
                userMeta: {
                    whatsNewDismissed: {
                        ...whatsNewDismissed,
                        [versionId]: true,
                    },
                },
            })
        );
    };

    const handleCta = () => {
        const to = activeSlide?.cta?.to;
        if (to) history.push(to);
    };

    if (isDismissed || !activeSlide) return null;

    const { title, description, cta, Icon, forceIconBlue = false } = activeSlide;

    return (
        <aside
            className={classNames(classes.carousel, className)}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            aria-roledescription="carousel"
            aria-label={`${eyebrow} announcements`}
            data-whats-new-carousel
        >
            <div className={classes.body}>
                {Icon && (
                    <div
                        className={classNames(classes.iconWrap, { [classes.forceBlue]: forceIconBlue })}
                        aria-hidden="true"
                    >
                        <Icon />
                    </div>
                )}

                <div className={classNames(classes.copy, { [classes.fading]: isFading })}>
                    <div className={classes.eyebrow}>{eyebrow}</div>
                    <div className={classes.titleRow}>
                        <h3 className={classes.title}>{title}</h3>
                        {cta?.label && (
                            <button type="button" className={classes.cta} onClick={handleCta}>
                                {cta.label}
                            </button>
                        )}
                    </div>
                    <p className={classes.description}>{description}</p>
                </div>
            </div>

            <div className={classes.controls}>
                {slideCount > 1 && (
                    <>
                        <div className={classes.dots} role="tablist" aria-label="Announcement slides">
                            {slides.map((slide, index) => (
                                <button
                                    key={slide.id || index}
                                    type="button"
                                    role="tab"
                                    aria-selected={index === activeIndex}
                                    aria-label={`Show slide ${index + 1}: ${slide.title}`}
                                    className={classNames(classes.dot, {
                                        [classes.dotActive]: index === activeIndex,
                                    })}
                                    onClick={() => goTo(index)}
                                />
                            ))}
                        </div>

                        <div className={classes.arrows}>
                            <button
                                type="button"
                                className={classes.arrow}
                                onClick={goPrev}
                                aria-label="Previous announcement"
                            >
                                <ArrowDown
                                    fill="currentColor"
                                    className={classes.arrowPrev}
                                    style={{ transform: "rotate(90deg)" }}
                                />
                            </button>
                            <button
                                type="button"
                                className={classes.arrow}
                                onClick={goNext}
                                aria-label="Next announcement"
                            >
                                <ArrowDown
                                    fill="currentColor"
                                    className={classes.arrowNext}
                                    style={{ transform: "rotate(-90deg)" }}
                                />
                            </button>
                        </div>
                    </>
                )}

                <button
                    type="button"
                    className={classes.dismiss}
                    onClick={handleDismiss}
                    aria-label="Dismiss announcements"
                >
                    <CloseIcon />
                </button>
            </div>
        </aside>
    );
};

export default WhatsNewCarousel;
